import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Paper,
  IconButton,
  CircularProgress,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import { useForm } from "react-hook-form";
import { useParams, useNavigate } from "react-router-dom";

import WSTextField, { TextType } from "../../../Components/TextBox/WSTextBox";
import WSPhone from "../../../Components/Phone/WSPhone";
import { callAPI, customValidation, Method, validations } from "../../../commonFunctions/CommonFunctions";
import { useSnackbar } from "../../../Components/Snackbar/SnackBarContext";
import ConfirmationPopup from "../../../Components/Confirmation/Confirmation";
import WSDateField from "../../../Components/DateField/WSDateField";



const Expense = () => {
  const [loading, setLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedId, setSelectedId] = useState<string | number>(0);
  const [isPopupOpen, setPopupOpen] = useState(false);
  const { showMessage } = useSnackbar();
  const params = useParams();
  const id = params.id;
  const navigate = useNavigate();

  

  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      Name: "",
      Date: "",
      Amount: "",
      Remarks: "",
      
    },
  });

  useEffect(() => {

    if (!id || id === "undefined" || id === "null" || id.trim() === "") {
      setIsEditMode(false);
      reset();
      return;
    }

    setIsEditMode(true);
    fetchExpenseData(id);
  }, [id, showMessage, reset]);

  const fetchExpenseData = async (expenseId: string) => {
    try {
      setLoading(true);

      const response = await callAPI(`expense/${expenseId}`, Method.GET);

      if (response?.success && response.data) {
        const expenseData = response.data;

        reset({
          Name: expenseData.Name || "",
          Date: expenseData.Date || "",
          Amount: expenseData.Amount || "",
          Remarks: expenseData.Remarks || "",
         
        });
      } else {
        console.error("❌ No data in response:", response);
        showMessage(response?.message || `Expense ${expenseId} not found`, "error");
        setIsEditMode(false);
      }
    } catch (error: any) {
      console.error("❌ Fetch error:", error.response?.data || error.message);
      showMessage("Failed to load Expense data", "error");
      setIsEditMode(false);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data: any) => {
    try {
      setLoading(true);
      const requestBody = {
        Name: data.Name?.trim(),
        Date: data.Date || new Date().toISOString(),
        Amount: data.Amount || "",
        Remarks: data.Remarks?.trim() || "",
       
      };


      let response;
      if (isEditMode && id) {
        response = await callAPI(`expense/${id}`, Method.PATCH, requestBody);
      } else {
        response = await callAPI("expense/create", Method.POST, requestBody);
      }

      if (response?.success) {
        showMessage(
          isEditMode
            ? "expenses updated successfully!"
            : "expenses created successfully!",
          "success"
        );
        navigate("/expense/list");
      } else {
        showMessage(response?.message || "Operation failed", "error");
      }
    } catch (error: any) {
      console.error("❌ Submit error:", error);
      showMessage(error?.message || "Operation failed", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = () => {
    if (!id) return;
    setSelectedId(id);
    setPopupOpen(true);
  };

  const handleBack = () => {
    navigate("/expense/list");
  };

  return (
    <Box sx={{ p: 2, background: "#f0f0f0", minHeight: "100vh" }}>
      {}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Box display="flex" alignItems="center" gap={2}>
          
          <Typography sx={{ fontSize: "20px", fontWeight: 600 }}>
            {isEditMode ? "Expenses Management" : "Expenses Management"}
          </Typography>
          
        </Box>

        <Box>
         
          {isEditMode && id && (
            <IconButton
              onClick={handleDeleteClick}
              disabled={loading}
              sx={{
                background: "black",
                color: "#fff",
                "&:hover": { background: "black" },
              }}
            >
              {loading ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon fontSize="small" />}
            </IconButton>
          )}
        </Box>
      </Box>

      {}
      <Paper
        sx={{
          borderRadius: "16px",
          p: 3,
          background: "#fafafa",
          position: "relative",
        }}
      >
        {loading && (
          <Box sx={{
            position: "absolute",
            top: 0, left: 0, right: 0, bottom: 0,
            background: "rgba(255,255,255,0.9)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10,
            borderRadius: "16px",
          }}>
            <CircularProgress />
          </Box>
        )}


        {}
        <Box display="flex" justifyContent="space-between" mb={7}>
          <Typography sx={{ fontSize: "18px", fontWeight: 500 }}>
            {isEditMode ? "Expenses Details" : "Expenses Details"}
          </Typography>
          
          <IconButton
            sx={{ background: "#000", color: "#fff" }}
            onClick={handleSubmit(onSubmit)}
            disabled={!isValid || loading}
          >
            <SaveIcon fontSize="small" />
          </IconButton>
        </Box>

        <form noValidate>
          <Grid container rowSpacing={8} columnSpacing={5}>
            <Grid item xs={4}>
              <WSTextField
                name="Name"
                label="Name"
                id="Name"
                control={control}
                rules={{ required: "name is required" }}
              />
            </Grid>

            <Grid item xs={4}>
              <WSDateField
              name="Date"
              label="Date"
              control={control}
              id="Date"
              rules={undefined}
            />
            </Grid>

           

            <Grid item xs={12} md={4}>
              <WSTextField
                name="Amount"
                label="Amount"
                id="Amount"
                control={control}
                type={TextType.AllowOnlyNumber}
              />
            </Grid>

            <Grid item xs={4}>
              <WSTextField
                name="Remarks"
                label="Remarks"
                id="Remarks"
                control={control}
              />
            </Grid>
          </Grid>
        </form>
      </Paper>

      {}
      <ConfirmationPopup
        type="DELETE"
        apiUrl="expense/delete" // ensure this matches your warehouse delete endpoint
        id={selectedId}
        confirmMessage="Are you sure you want to delete this expense?"
        open={isPopupOpen}
        onClose={() => setPopupOpen(false)}
        onConfirm={async (data) => {
          if (!selectedId) return;

          if (data?.success === true) {
           

            showMessage("Expense deleted successfully!", "success");
            navigate("/expense/list");
          } else {
            showMessage(data?.message || "Delete failed", "error");
          }

          setPopupOpen(false);
        }}
      />
    </Box>
  );
};

export default Expense;
